import React, {useCallback, useEffect, useState} from 'react';

import {
  Alert,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Stack,
} from '@zoom/react-ui';
import {sub} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {sendVerificationEmail} from 'Api/Calendar';
import {captchaResultType} from 'Components/Captcha/CaptchaDownload';
import {ZmPrimaryBtn, ZmQuaternaryBtn, ZmSecondaryBtn} from 'Components/ZmButtons';
import {ZmTextField} from 'Components/ZmInputs';
import {CaptchaError} from 'Error/CaptchaError';
import {snackbarActions} from 'Store/SnackbarStore';
import {useQueryWithCaptchaPasscode} from 'Utils/Captcha';


const remapArgsSendVerify = (calendarId, email, captchaPasscode) => {
  return sendVerificationEmail(calendarId, {
    host: calendarId,
    email: email,
    captcha: captchaPasscode,
  });
};

const VERIFY_CODE_EXP_MIN = 5;
const VERIFY_CODE_LEN = 6;

/**
 * Email Verification Code intake component
 * @param {*} props
 * @return {ReactElement}
 */
export function VerificationForm(props) {
  const {
    calendarId,
    email,
    errorState,
    isSubmitting,
    onCancel,
    onSubmit,
  } = props;
  const {t} = useTranslation();

  const dispatch = useDispatch();
  const [isSendingVerify, setIsSendingVerify] = useState(false);

  const doSendVerify = useQueryWithCaptchaPasscode(remapArgsSendVerify);

  const sendVerification = useCallback(async (email) => {
    setIsSendingVerify(true);
    try {
      await doSendVerify(calendarId, email);
      window.sessionStorage.setItem('verifySentTime', new Date().toISOString());
      window.sessionStorage.setItem('verifyEmail', email);
      dispatch(snackbarActions.addSnackbar({
        message: 'verifyForm.emailSendSuccess',
        severity: 'success',
      }));
    } catch (e) {
      console.error('email failed to send', e);
      if (e instanceof CaptchaError && e.message === captchaResultType.error) {
        dispatch(snackbarActions.addSnackbar({
          message: 'verifyForm.captchaVerifyFailure',
          severity: 'warning',
        }));
      } else {
        dispatch(snackbarActions.addSnackbar({
          message: 'verifyForm.emailSendFailure',
          severity: 'warning',
        }));
      }
    }
    setIsSendingVerify(false);
  }, [dispatch, calendarId, doSendVerify]);

  useEffect(() => {
    // When the component is mounted, automatically send the verification email if:
    // - The last verification email was sent more than THRESHOLD minutes ago +
    // - The email is the same email
    const lastVerifySent = window.sessionStorage.getItem('verifySentTime');
    const lastVerifyEmail = window.sessionStorage.getItem('verifyEmail');
    const expiredTime = sub(new Date(), {minutes: VERIFY_CODE_EXP_MIN});
    if (!lastVerifySent || lastVerifyEmail !== email || (expiredTime > new Date(lastVerifySent))) {
      sendVerification(email);
    } else {
      console.log('not auto sending verify code');
    }
  }, [sendVerification, email]);

  const [passcode, setPasscode] = useState('');

  return (
    <>
      {errorState.isError &&
        <Alert severity="error" sx={{borderRadius: '10px'}}>
          {errorState.reason}
        </Alert>
      }
      <DialogTitle
        variant="h5"
        textAlign="center"
      >
        {t('verifyForm.verifyTitle')}
      </DialogTitle>
      <DialogContent sx={{textAlign: 'center'}}>
        <DialogContentText variant="body1" lineHeight="1.25rem">
          {t('verifyForm.verifyPrompt')} <b>{email}</b>
        </DialogContentText>

        <ZmTextField
          autoFocus
          id="verCode"
          value={passcode}
          onChange={(e) => {
            setPasscode(e.target.value);
          }}
          inputProps={{maxLength: VERIFY_CODE_LEN}}
          required
          sx={{marginY: '1.5rem', width: '180px', input: {textAlign: 'center'}}}
          placeholder={t('verifyForm.verifyCodePlaceholder')}
        />

        <Stack gap={1} flexDirection="row" alignItems="center" justifyContent="center">
          <DialogContentText variant="body1" lineHeight="1.25rem">
            {t('verifyForm.codeNotReceived')}
          </DialogContentText>
          <ZmQuaternaryBtn
            size="small"
            disabled={isSendingVerify}
            onClick={() => sendVerification(email)}
            sx={{fontWeight: 500, height: '2rem'}}
            startIcon={(isSendingVerify) ? <CircularProgress size=".875rem" color="inherit" /> : null}
          >
            {(isSendingVerify) ? t('verifyForm.sendingCode') : t('verifyForm.resendCode')}
          </ZmQuaternaryBtn>
        </Stack>
        {/* <DialogContentText variant="body1">
        Having trouble?
        <Link
          underline="none"
          color="primary"
          // TODO functionality for the gethelp
        >
          <b> Get help</b>
        </Link>
      </DialogContentText> */}
      </DialogContent>
      <DialogActions>
        <ZmSecondaryBtn
          size="small"
          onClick={onCancel}
          disabled={isSubmitting}
          sx={{height: '2rem'}}
        >
          {t('verifyForm.cancel')}
        </ZmSecondaryBtn>
        <ZmPrimaryBtn
          size="small"
          onClick={() => {
            onSubmit(passcode);
          }}
          disabled={(!passcode || isSubmitting || passcode?.length !== VERIFY_CODE_LEN)}
          sx={{height: '2rem'}}
        >
          {t('verifyForm.submit')}
        </ZmPrimaryBtn>
      </DialogActions>
    </>
  );
};
